import { useState, useEffect, useCallback } from 'react';
import './ParkingTable.css';
import ReservationEditForm from './ReservationEditForm';
import { useSSE, getWorkerUrl } from '../utils/useSSE';
import { getApiUrl } from '../utils/api';

const DAYS_TO_SHOW = 11; // Today + next 10 days

/**
 * Gets the ID token from MSAL instance
 * @param {Object} msalInstance - MSAL instance
 * @returns {Promise<string|null>} - ID token or null if unavailable
 */
async function getIdToken(msalInstance) {
  try {
    // Get all accounts and use the first one
    const accounts = msalInstance.getAllAccounts();
    if (!accounts || accounts.length === 0) {
      return null;
    }
    const account = accounts[0];
    console.log('Getting ID token for account:', account);
    // Try to get token silently
    const response = await msalInstance.acquireTokenSilent({
      scopes: ['User.Read'],
      account: account,
    });
    console.log('ID token response:', response);
    return response.idToken || null;
  } catch (error) {
    console.error('Failed to get ID token:', error);
    return null;
  }
}

/**
 * Component to display parking slots that have a default owner
 * Shows these slots in a table format underneath the main ParkingTable
 * Default owners can release their slots to make them available for reservation
 */
function DefaultOwnerParkingTable({ user, powerUserMode, msalInstance }) {
  const [parkingSlots, setParkingSlots] = useState([]);
  const [allParkingSlots, setAllParkingSlots] = useState([]); // Store all slots for form dropdown
  const [releases, setReleases] = useState({});
  const [reservations, setReservations] = useState({});
  const [workingDays, setWorkingDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingReservation, setEditingReservation] = useState(null);
  const [editingDate, setEditingDate] = useState(null);
  const [editingSlotLabel, setEditingSlotLabel] = useState(null);

  // Get current date in Finland timezone
  const getTodayInFinland = () => {
    const now = new Date();
    // Convert to Finland timezone (Europe/Helsinki)
    const finlandDateStr = now.toLocaleString('en-US', {
      timeZone: 'Europe/Helsinki',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    // Parse MM/DD/YYYY format
    const [month, day, year] = finlandDateStr.split(',')[0].split('/');
    const finlandDate = new Date(year, month - 1, day, 0, 0, 0, 0);
    return finlandDate;
  };

  // Calculate working days (Monday-Friday) starting from today in Finland timezone
  const calculateWorkingDays = () => {
    const days = [];
    let current = getTodayInFinland();

    while (days.length < DAYS_TO_SHOW) {
      const dayOfWeek = current.getDay();
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        days.push(new Date(current));
      }
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  // Initialize working days
  useEffect(() => {
    const updateDays = () => {
      setWorkingDays(calculateWorkingDays());
    };

    updateDays();

    // Check for midnight to refresh days
    const now = new Date();
    const midnight = new Date(now);
    midnight.setDate(midnight.getDate() + 1);
    midnight.setHours(0, 0, 0, 0);
    const msUntilMidnight = midnight - now;

    const midnightTimer = setTimeout(() => {
      updateDays();
      // Set up daily interval after first midnight
      const dailyInterval = setInterval(updateDays, 24 * 60 * 60 * 1000);
      return () => clearInterval(dailyInterval);
    }, msUntilMidnight);

    return () => clearTimeout(midnightTimer);
  }, []);

  // Load parking slots from API (only slots with default_owner)
  useEffect(() => {
    let isInitialLoad = true;

    const loadParkingSlots = async (showLoading = false) => {
      try {
        if (showLoading) {
          setLoading(true);
        }
        const idToken = await getIdToken(msalInstance);
        if (!idToken) {
          throw new Error('Failed to get authentication token');
        }

        const response = await fetch(getApiUrl('/api/parkingSlots'), {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load parking slots: ${response.statusText}`);
        }

        const data = await response.json();
        // Store all slots for form dropdown
        setAllParkingSlots(data);
        // Filter to only slots with default_owner and sort by display_order
        const defaultOwnerSlots = data
          .filter((slot) => slot.default_owner && slot.default_owner.trim() !== '')
          .sort((a, b) => a.display_order - b.display_order);
        setParkingSlots(defaultOwnerSlots);
      } catch (e) {
        console.error('Failed to load parking slots:', e);
        if (showLoading) {
          setError('Failed to load parking slots. Please refresh the page.');
        }
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    };

    // Load immediately with loading state
    loadParkingSlots(true);

    // Handle visibility change to refresh when window becomes active
    const handleVisibilityChange = () => {
      if (!document.hidden && !isInitialLoad) {
        // Refresh parking slots when window becomes active (show loading spinner)
        loadParkingSlots(true);
      }
      isInitialLoad = false;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Load releases from API
  const loadReleases = async () => {
    try {
      // Get ID token for authentication
      const idToken = await getIdToken(msalInstance);
      if (!idToken) {
        console.error('Unable to get ID token for authentication');
        return;
      }

      const response = await fetch(getApiUrl('/api/slotReleases'), {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load releases: ${response.statusText}`);
      }

      const data = await response.json();

      // Only update if data has changed
      setReleases((prevReleases) => {
        const prevJson = JSON.stringify(prevReleases);
        const newJson = JSON.stringify(data);
        if (prevJson !== newJson) {
          return data;
        }
        return prevReleases;
      });
    } catch (e) {
      console.error('Failed to load releases:', e);
    }
  };

  // Load releases on initial mount
  useEffect(() => {
    loadReleases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msalInstance]);

  // Load reservations from API
  const loadReservations = async () => {
    try {
      // Get ID token for authentication
      const idToken = await getIdToken(msalInstance);
      if (!idToken) {
        console.error('Unable to get ID token for authentication');
        return;
      }

      const response = await fetch(getApiUrl('/api/reservations'), {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load reservations: ${response.statusText}`);
      }

      const data = await response.json();

      // Only update if data has changed
      setReservations((prevReservations) => {
        const prevJson = JSON.stringify(prevReservations);
        const newJson = JSON.stringify(data);
        if (prevJson !== newJson) {
          return data;
        }
        return prevReservations;
      });
    } catch (e) {
      console.error('Failed to load reservations:', e);
    }
  };

  // Load reservations on initial mount
  useEffect(() => {
    loadReservations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msalInstance]);

  // Connect to SSE for real-time updates
  const workerUrl = getWorkerUrl();
  const [sseError, setSseError] = useState(false);
  
  const handleSSEMessage = useCallback((eventData) => {
    // Handle SSE events - use received data directly instead of fetching
    if (eventData.type === 'reservations' && eventData.data?.data) {
      // Update reservations directly with received data
      setReservations(eventData.data.data);
    } else if (eventData.type === 'slotReleases' && eventData.data?.data) {
      // Update releases directly with received data
      setReleases(eventData.data.data);
    } else if (eventData.type === 'parkingSlots' && eventData.data?.data) {
      // Update parking slots directly with received data
      const slotsData = eventData.data.data;
      setAllParkingSlots(slotsData);
      const defaultOwnerSlots = slotsData
        .filter((slot) => slot.default_owner && slot.default_owner.trim() !== '')
        .sort((a, b) => a.display_order - b.display_order);
      setParkingSlots(defaultOwnerSlots);
    }
  }, []); // State setters are stable

  const handleSSEError = useCallback((error) => {
    console.error('SSE connection error:', error);
    setSseError(true);
  }, []);

  useSSE(workerUrl, handleSSEMessage, handleSSEError);

  // Fallback to polling if SSE fails or is not configured
  useEffect(() => {
    if (!workerUrl || sseError) {
      console.log('Using polling fallback for releases and reservations');
      const releaseInterval = setInterval(() => {
        loadReleases();
      }, 5000);
      const reservationInterval = setInterval(() => {
        loadReservations();
      }, 5000);
      return () => {
        clearInterval(releaseInterval);
        clearInterval(reservationInterval);
      };
    }
  }, [workerUrl, sseError]); // Functions are stable enough

  // Format date as YYYY-MM-DD (using local timezone)
  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date for display
  const formatDate = (date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}.${month}.`;
  };

  // Get weekday name
  const getWeekday = (date) => {
    const days = ['Su', 'Ma', 'Ti', 'Ke', 'To', 'Pe', 'La'];
    return days[date.getDay()];
  };

  // Check if user has multiple reservations for this day
  const getUserReservationsForDay = (dateKey) => {
    if (!reservations[dateKey]) return [];
    // Get all reservations for this user on this day
    return Object.values(reservations[dateKey]).filter(
      (reservation) => reservation.user_id === user.user_id
    );
  };

  // Check if user has multiple reservations for this day
  const hasMultipleReservationsForDay = (dateKey) => {
    return getUserReservationsForDay(dateKey).length > 1;
  };

  // Format location for display
  const formatLocation = (location) => {
    if (location === 'ground_floor') return 'Ground Floor';
    if (location === 'basement') return 'Basement';
    return location;
  };

  // Create or delete reservation via API
  const updateReservation = async (action, date, slotLabel) => {
    try {
      // Get ID token for authentication
      const idToken = await getIdToken(msalInstance);
      if (!idToken) {
        throw new Error('Unable to get ID token for authentication');
      }

      const response = await fetch(getApiUrl('/api/reservations'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          action,
          date: formatDateKey(date),
          slot_id: slotLabel,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to update reservation');
      }

      // Reload reservations after successful update
      const reloadIdToken = await getIdToken(msalInstance);
      if (reloadIdToken) {
        const reloadResponse = await fetch(getApiUrl('/api/reservations'), {
          headers: {
            Authorization: `Bearer ${reloadIdToken}`,
          },
        });
        if (reloadResponse.ok) {
          const data = await reloadResponse.json();
          setReservations(data);
        }
      }

      return result;
    } catch (e) {
      console.error('Failed to update reservation:', e);
      throw e;
    }
  };

  // Reload reservations and releases after save
  const handleReservationSave = async () => {
    try {
      const idToken = await getIdToken(msalInstance);
      if (idToken) {
        // Reload reservations
        const reloadResponse = await fetch(getApiUrl('/api/reservations'), {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });
        if (reloadResponse.ok) {
          const data = await reloadResponse.json();
          setReservations(data);
        }

        // Reload releases
        const reloadReleasesResponse = await fetch(getApiUrl('/api/slotReleases'), {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        });
        if (reloadReleasesResponse.ok) {
          const releasesData = await reloadReleasesResponse.json();
          setReleases(releasesData);
        }
      }
    } catch (e) {
      console.error('Failed to reload data:', e);
    }
  };

  // Handle slot click - release, claim back, or reserve
  const handleSlotClick = async (date, slotLabel, event) => {
    const dateKey = formatDateKey(date);
    const isReleased = releases[dateKey]?.[slotLabel];
    const reservation = reservations[dateKey]?.[slotLabel];
    const slot = parkingSlots.find((s) => s.label === slotLabel);
    const isMySlot = user && slot && slot.default_owner === user.user_id;
    const isPowerUserClickable = user.isPowerUser && powerUserMode;

    // If PowerUser mode is enabled, always open edit form
    if (isPowerUserClickable) {
      setEditingReservation(reservation || null);
      setEditingDate(date);
      setEditingSlotLabel(slotLabel);
      return;
    }

    // Regular behavior for non-PowerUser or PowerUser mode OFF
    // If there's a reservation
    if (reservation) {
      const isOwnReservation = reservation.user_id === user.user_id;

      // If it's the user's own reservation, allow cancellation
      if (isOwnReservation) {
        try {
          await updateReservation('delete', date, slotLabel);
        } catch (e) {
          alert(`Failed to cancel reservation: ${e.message}`);
        }
        return;
      }

      // Someone else's reservation
      alert(`This slot is reserved by ${reservation.family_name}`);
      return;
    }

    // If it's the default owner's slot OR PowerUser wants to release
    if (isMySlot || (user.isPowerUser && powerUserMode)) {
      // Release or claim back
      try {
        // Get ID token for authentication
        const idToken = await getIdToken(msalInstance);
        if (!idToken) {
          throw new Error('Unable to get ID token for authentication');
        }

        const action = isReleased ? 'delete' : 'create';
        const response = await fetch(getApiUrl('/api/slotReleases'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            action,
            date: dateKey,
            slot_id: slotLabel,
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Failed to update release');
        }

        // Reload releases after successful update
        const reloadIdToken = await getIdToken(msalInstance);
        if (reloadIdToken) {
          const reloadResponse = await fetch(getApiUrl('/api/slotReleases'), {
            headers: {
              Authorization: `Bearer ${reloadIdToken}`,
            },
          });
          if (reloadResponse.ok) {
            const data = await reloadResponse.json();
            setReleases(data);
          }
        }
      } catch (e) {
        console.error('Failed to update release:', e);
        alert(`Failed to ${isReleased ? 'claim' : 'release'} slot: ${e.message}`);
      }
      return;
    }

    // If slot is released and user is not the default owner, allow reservation
    if (isReleased) {
      // Multiple bookings per day are now allowed - removed the check

      // Make reservation
      try {
        await updateReservation('create', date, slotLabel);
      } catch (e) {
        alert(`Failed to create reservation: ${e.message}`);
      }
      return;
    }

    // Slot is not released and user is not the default owner - no action
  };

  // Get owner name from reservations data (for Power users)
  const getOwnerName = (ownerUserId) => {
    if (!ownerUserId) return null;

    // Look through all reservations to find the owner's name
    for (const dateKey in reservations) {
      for (const slotLabel in reservations[dateKey]) {
        const reservation = reservations[dateKey][slotLabel];
        if (reservation.user_id === ownerUserId) {
          return reservation.family_name;
        }
      }
    }
    try {
      const lastName = ownerUserId.split('.')[1].split('@')[0];
      console.log('lastName', lastName);
      return `${lastName.charAt(0).toUpperCase()}${lastName.slice(1).toLowerCase()}`;
    } catch (e) {
      console.error('Failed to get owner name:', e);
      return null;
    }
  };

  // Get slot status for display
  const getSlotStatus = (date, slot) => {
    const dateKey = formatDateKey(date);
    const isMySlot = user && slot.default_owner === user.user_id;
    const isReleased = releases[dateKey]?.[slot.label];
    const reservation = reservations[dateKey]?.[slot.label];
    const hasMultiple = hasMultipleReservationsForDay(dateKey);

    // If there's a reservation, show it
    if (reservation) {
      const isOwnReservation = reservation.user_id === user.user_id;
      if (isOwnReservation) {
        return { 
          status: hasMultiple ? 'multiple-mine' : 'mine', 
          text: user.family_name, 
          clickable: true 
        };
      }
      // PowerUsers can click any reservation when PowerUser mode is enabled
      const isPowerUserClickable = user.isPowerUser && powerUserMode;
      return { 
        status: 'taken', 
        text: reservation.family_name, 
        clickable: isPowerUserClickable 
      };
    }

    // No reservation - check if slot is released
    if (isReleased) {
      // Slot is released (available for anyone to reserve)
      // PowerUsers can always click when PowerUser mode is enabled
      const isPowerUserClickable = user.isPowerUser && powerUserMode;
      return { 
        status: 'available', 
        text: 'Released', 
        clickable: true 
      };
    }

    // Slot is not released
    if (!isMySlot) {
      // Someone else's default slot, not released
      // For Power users with PowerUser mode enabled, show the owner's name and make clickable
      const isPowerUserClickable = user?.isPowerUser && powerUserMode;
      if (isPowerUserClickable) {
        const ownerName = getOwnerName(slot.default_owner);
        const displayText = ownerName || slot.default_owner || 'Reserved';
        return { status: 'taken', text: displayText, clickable: true };
      }
      return { status: 'taken', text: 'Reserved', clickable: false };
    }

    // My slot, not released (mine)
    // PowerUsers can always click when PowerUser mode is enabled
    return { 
      status: 'mine', 
      text: user.family_name, 
      clickable: true 
    };
  };

  if (loading) {
    return (
      <div className="parking-table-container">
        <div style={{ textAlign: 'center', padding: '2rem' }}>Loading default owner slots...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="parking-table-container">
        <div style={{ textAlign: 'center', padding: '2rem', color: '#dc2626' }}>{error}</div>
      </div>
    );
  }

  // Don't render if there are no slots with default owners
  if (parkingSlots.length === 0) {
    return null;
  }

  return (
    <div className="parking-table-container" style={{ marginTop: '2rem' }}>
      {editingDate && editingSlotLabel && (
        <ReservationEditForm
          reservation={editingReservation}
          date={editingDate}
          slotLabel={editingSlotLabel}
          parkingSlots={allParkingSlots}
          releases={releases}
          slot={parkingSlots.find((s) => s.label === editingSlotLabel)}
          onClose={() => {
            setEditingReservation(null);
            setEditingDate(null);
            setEditingSlotLabel(null);
          }}
          onSave={handleReservationSave}
          msalInstance={msalInstance}
        />
      )}
      <h2 style={{ marginBottom: '1rem', color: '#023d43', fontSize: '1.25rem' }}>
        🏢 Slots with Default Owners
      </h2>
      <div className="parking-table-wrapper">
        <table className="parking-table">
          <thead>
            <tr>
              <th className="date-column">Date</th>
              {parkingSlots.map((slot) => (
                <th key={slot.id} className="slot-column">
                  <div className="slot-header-label">
                    {slot.label}
                    {slot.ev_charging === 1 && (
                      <span className="ev-icon" title="EV Charging Available">
                        ⚡
                      </span>
                    )}
                  </div>
                  <div className="slot-header-location">{formatLocation(slot.location)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {workingDays.map((date) => {
              const dateKey = formatDateKey(date);

              return (
                <tr key={dateKey}>
                  <td className="date-column">
                    <div className="date-display">{formatDate(date)}</div>
                    <div className="weekday-display">{getWeekday(date)}</div>
                  </td>
                  {parkingSlots.map((slot) => {
                    const { status, text, clickable } = getSlotStatus(date, slot);
                    const isMySlot = user && slot.default_owner === user.user_id;
                    const isReleased = releases[formatDateKey(date)]?.[slot.label];
                    const reservation = reservations[formatDateKey(date)]?.[slot.label];
                    const dateKey = formatDateKey(date);
                    const hasMultiple = hasMultipleReservationsForDay(dateKey);
                    const isPowerUserClickable = user.isPowerUser && powerUserMode;

                    let title = '';
                    if (reservation) {
                      const isOwnReservation = reservation.user_id === user.user_id;
                      if (isOwnReservation) {
                        title = hasMultiple 
                          ? 'Click to cancel your reservation (Multiple bookings)'
                          : 'Click to cancel your reservation';
                      } else {
                        title = isPowerUserClickable
                          ? `Click to edit reservation (PowerUser) - Reserved by ${reservation.family_name}`
                          : `Reserved by ${reservation.family_name}`;
                      }
                    } else if (isMySlot || isPowerUserClickable) {
                      if (isPowerUserClickable && !isMySlot) {
                        title = `Click to edit/create reservation (PowerUser). Use release button in form to ${isReleased ? 'claim back' : 'release'} slot`;
                      } else {
                        title = isPowerUserClickable
                          ? `Click to edit/create reservation (PowerUser). Use release button in form to ${isReleased ? 'claim back' : 'release'} slot`
                          : `Click to ${isReleased ? 'claim back' : 'release'} this slot`;
                      }
                    } else if (isReleased) {
                      title = isPowerUserClickable
                        ? 'Click to create/edit reservation (PowerUser)'
                        : 'Click to reserve this released slot';
                    } else {
                      // For Power users with PowerUser mode enabled, show owner's name in tooltip if available
                      if (isPowerUserClickable) {
                        const ownerName = getOwnerName(slot.default_owner);
                        title = ownerName
                          ? `Click to create/edit reservation (PowerUser) - Default owner: ${ownerName} (${slot.default_owner})`
                          : `Click to create/edit reservation (PowerUser) - Default owner: ${slot.default_owner}`;
                      } else {
                        title = 'Reserved';
                      }
                    }

                    const slotClassName = `slot ${status}${!clickable ? ' disabled' : ''}${isPowerUserClickable && clickable ? ' clickable' : ''}`;

                    return (
                      <td
                        key={slot.id}
                        className={slotClassName}
                        onClick={clickable ? (e) => handleSlotClick(date, slot.label, e) : undefined}
                        title={title}
                      >
                        {text}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DefaultOwnerParkingTable;
